import { Bag, ProductItem } from "@repo/apis/bag";
import {
  getLocalStorage,
  setLocalStorage,
  removeLocalStorage,
} from "@repo/utils/storage";
import type { BagSdkConfig } from "../sdk";

const BAG_CACHE_KEY = "Asos.Commerce.Bag.Sdk";

type SummaryStorage = {
  bagId: string | null;
  customerId: string;
  sessionToken: string | null;
  totalPriceValue: number;
  totalPriceText: string;
  currency: string;
  totalQuantity: number;
  origin: string | null;
  expires: number;
  items: { id: string; productId: number; quantity: number }[];
};

export type SummaryState = SummaryStorage & {
  hasExpired: boolean;
};

type SummaryStorageRecord = Record<string, SummaryStorage>;

const twoHours = () => Date.now() + 2 * 60 * 60 * 1000;

const buildSummaryKey = ({
  customerAnonymousId,
  store,
  country,
}: {
  customerAnonymousId: string;
  store: string;
  country: string;
}) => `${customerAnonymousId}:::${store}:::${country}:::summary`;

const clear = () => {
  removeLocalStorage(BAG_CACHE_KEY);
};

const init = async (config: BagSdkConfig) => {
  const { customerAnonymousId, customerId } = await config.getIdTokenClaims();

  const key = buildSummaryKey({
    customerAnonymousId,
    store: config.store,
    country: config.country,
  });

  const existingState = getLocalStorage<SummaryStorageRecord>(BAG_CACHE_KEY);

  const summary: SummaryStorage = {
    bagId: null,
    customerId,
    sessionToken: null,
    totalPriceValue: 0,
    totalPriceText: "0",
    currency: config.currency,
    totalQuantity: 0,
    origin: null,
    expires: twoHours(),
    items: [],
  };

  setLocalStorage(BAG_CACHE_KEY, {
    ...existingState,
    [key]: summary,
  });
};

const getSummary = async (
  config: BagSdkConfig,
): Promise<SummaryState | null> => {
  const { customerAnonymousId } = await config.getIdTokenClaims();

  const key = buildSummaryKey({
    customerAnonymousId,
    store: config.store,
    country: config.country,
  });

  const existingState = getLocalStorage<SummaryStorageRecord>(BAG_CACHE_KEY);

  const state = existingState?.[key];

  if (!state) return null;

  return {
    ...state,
    hasExpired: Date.now() > state.expires,
  };
};

const put = async (
  config: BagSdkConfig,
  {
    bag,
    sessionToken,
  }: {
    bag: Bag;
    sessionToken: string;
  },
) => {
  const { customerAnonymousId, customerId } = await config.getIdTokenClaims();

  const key = buildSummaryKey({
    customerAnonymousId,
    store: config.store,
    country: config.country,
  });

  const existingState = getLocalStorage<SummaryStorageRecord>(BAG_CACHE_KEY);

  const summary: SummaryStorage = {
    bagId: bag.id,
    customerId,
    sessionToken,
    totalPriceValue: bag.summary?.totalPrice.value || 0,
    totalPriceText: bag.summary?.totalPrice.text || "0",
    currency: config.currency,
    totalQuantity: bag.summary?.totalQuantity || 0,
    origin: bag.origin || null,
    expires: twoHours(),
    items: (bag.items ?? [])
      .filter((item): item is ProductItem => item.itemType === "Product")
      .map(({ id, productId, quantity }) => ({ id, productId, quantity })),
  };

  setLocalStorage(BAG_CACHE_KEY, {
    ...existingState,
    [key]: summary,
  });
};

const signalRefresh = async (config: BagSdkConfig) => {
  const { customerAnonymousId } = await config.getIdTokenClaims();

  const key = buildSummaryKey({
    customerAnonymousId,
    store: config.store,
    country: config.country,
  });

  const existingState = getLocalStorage<SummaryStorageRecord>(BAG_CACHE_KEY);

  if (!existingState?.[key]) {
    return;
  }

  const summary: Partial<SummaryStorage | "expires"> = {
    ...existingState?.[key],
    expires: 0,
  };

  setLocalStorage(BAG_CACHE_KEY, {
    ...existingState,
    [key]: summary,
  });
};

export const cache = {
  clear,
  init,
  getSummary,
  put,
  signalRefresh,
};
